import axiosInstance from './Instance/axiosInstance';
import {
    AuditLogListResult,
    AuditLogQuery,
    AuditLogRecord,
    AuditLogSummaryItem,
    emptyAuditPagination,
} from '../types/auditLog';

/**
 * The API accepts comma separated lists for `collectionName` / `operation`,
 * so arrays are joined here and empty filters are dropped entirely.
 */
const toParams = (query: AuditLogQuery = {}): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {};

    if (query.collectionName?.length) params.collectionName = query.collectionName.join(',');
    if (query.operation?.length) params.operation = query.operation.join(',');
    if (query.documentId?.trim()) params.documentId = query.documentId.trim();
    if (query.userId?.trim()) params.userId = query.userId.trim();
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.sort) params.sort = query.sort;
    if (query.includeState) params.includeState = true;

    return params;
};

export const getAuditLogs = async (query: AuditLogQuery = {}): Promise<AuditLogListResult> => {
    const res = await axiosInstance.get('/audit-logs', { params: toParams(query) });
    return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { ...emptyAuditPagination, page: query.page ?? 1 },
    };
};

/** Only `create` operations, across every model. */
export const getAuditLogCreations = async (query: AuditLogQuery = {}): Promise<AuditLogListResult> => {
    const res = await axiosInstance.get('/audit-logs/creations', { params: toParams(query) });
    return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { ...emptyAuditPagination, page: query.page ?? 1 },
    };
};

/** Counts per model, per operation — used for the model filter options. */
export const getAuditLogSummary = async (query: AuditLogQuery = {}): Promise<AuditLogSummaryItem[]> => {
    const res = await axiosInstance.get('/audit-logs/summary', { params: toParams(query) });
    return res.data?.data ?? [];
};

/** A single log with its full `lastState` snapshot. */
export const getAuditLogById = async (id: string): Promise<AuditLogRecord | null> => {
    const res = await axiosInstance.get(`/audit-logs/${id}`);
    return res.data?.data ?? null;
};
